import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

import {
  FaArrowLeft,
  FaSearch,
  FaFilter,
  FaCog,
  FaInfoCircle,
  FaGasPump,
  FaCar,
  FaSave,
  FaCheckSquare,
  FaClock,
} from "react-icons/fa";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE = "https://nissi-india.com/api/hsdhr";

const toInputDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const getDefaultFromDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return toInputDate(date);
};

const getTodayDate = () => toInputDate(new Date());

const showVal = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;

const sanitizeNonNegative = (value) => {
  let next = String(value ?? "").replace(/[^\d.]/g, "");
  const firstDot = next.indexOf(".");
  if (firstDot !== -1) {
    next = `${next.slice(0, firstDot + 1)}${next.slice(firstDot + 1).replace(/\./g, "")}`;
  }
  return next;
};

const isNegativeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n < 0;
};

const EDIT_FIELDS = [
  { key: "curStartHr", label: "Cur Starting Hr" },
  { key: "curCloseHr", label: "Cur Closing Hr" },
  { key: "curStartKm", label: "Cur Starting KM" },
  { key: "curCloseKm", label: "Cur Closing KM" },
];

/*
 * width: 0 + minWidth: 100% stops the wide table (min-w-[1400px]) from
 * pushing its intrinsic width up into the page layout. The scroll box
 * still stretches to fill the card, and only the table scrolls sideways.
 */
const tableScrollStyle = { width: 0, minWidth: "100%" };

const VehicleHsdHrUpdation = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin, theme } = useContext(ThemeContext);

  const currentSkin = useMemo(
    () => getCurrentSkin(theme || skin),
    [theme, skin]
  );

  const baseColor = currentSkin?.baseColor || "blue";
  const baseShade = currentSkin?.baseShade || "600";

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchId =
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    localStorage.getItem("branch") ||
    "";
  const userId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": storedBranchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, storedBranchId, branch, userId]
  );

  const inputClass = `w-full max-w-full min-w-0 box-border px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const editInputClass = `w-24 max-w-full px-2.5 py-2 rounded-lg border text-sm text-center font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-slate-300 text-slate-900"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const cardClass = `w-full max-w-full min-w-0 rounded-2xl shadow-xl border overflow-hidden ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
      : `bg-white ${currentSkin?.glass || ""} border-gray-200`
  }`;

  const neutralButtonClass = `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition whitespace-nowrap ${
    isDark
      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
      : "border-slate-300 text-slate-700 hover:bg-slate-100"
  }`;

  const tableRowClass = `transition-colors ${
    isDark ? "bg-gray-800/20 hover:bg-gray-700/30" : "bg-white hover:bg-gray-50"
  }`;

  const tableCellClass = `px-4 py-3 text-sm whitespace-nowrap ${
    isDark ? "text-gray-300" : "text-gray-900"
  }`;

  const readOnlyCellClass = `px-4 py-3 text-sm whitespace-nowrap font-semibold ${
    isDark ? `text-${baseColor}-300` : `text-${baseColor}-700`
  }`;

  const [fromDate, setFromDate] = useState(getDefaultFromDate);
  const [toDate, setToDate] = useState(getTodayDate);
  const [selectedBranchId, setSelectedBranchId] = useState(() =>
    storedBranchId ? parseInt(storedBranchId, 10) : 0
  );
  const [vehicleCode, setVehicleCode] = useState("");

  const [branchList, setBranchList] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [rawData, setRawData] = useState([]);

  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editedRows, setEditedRows] = useState({});
  const [checkedRows, setCheckedRows] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  const data = useMemo(() => {
    return (rawData || []).map((row) => {
      const preStartHr = row.fld_Pre_HSDStartingHr ?? row.fld_preStartingHr ?? 0;
      const curStartHr = row.fld_Cur_HSDStartingHr ?? row.fld_curStartingHr ?? 0;
      const preCloseHr = row.fld_Pre_HSDClosingHr ?? row.fld_preClosingHr ?? 0;
      const curCloseHr = row.fld_Cur_HSDClosingHr ?? row.fld_curClosingHr ?? 0;
      const preStartKm = row.fld_Pre_HSDStartingKM ?? row.fld_preStartingKM ?? 0;
      const curStartKm = row.fld_Cur_HSDStartingKM ?? row.fld_curStartingKM ?? 0;
      const preCloseKm = row.fld_Pre_HSDClosingKM ?? row.fld_preClosingKM ?? 0;
      const curCloseKm = row.fld_Cur_HSDClosingKM ?? row.fld_curClosingKM ?? 0;

      const sidetId =
        row.fld_storesissuedetId ?? row.fld_storesissueDetid ?? row.SIdetId ?? null;

      const date = row.fld_date ?? row.fld_SIDate ?? row.fld_siDate ?? row.fld_Date ?? "";
      const qty = row.fld_qty ?? row.fld_Qty ?? null;

      const vehicleDisplay =
        (row.fld_vehiclecodename && String(row.fld_vehiclecodename).trim() !== ""
          ? row.fld_vehiclecodename
          : null) ||
        (row.fld_vehiclecode &&
        String(row.fld_vehiclecode).trim() !== "" &&
        row.fld_vehiclecode !== " : "
          ? row.fld_vehiclecode
          : null) ||
        (row.fld_vehicleCode ? row.fld_vehicleCode : null) ||
        "—";

      return {
        ...row,
        fld_storesissuedetId: sidetId,
        fld_branchid: row.fld_branchid ?? row.fld_branchId ?? selectedBranchId,
        fld_itemid: row.fld_itemid ?? row.fld_itemId ?? 0,
        fld_date: date,
        fld_qty: qty,
        fld_vehiclecodename: vehicleDisplay,
        fld_vehiclecode: vehicleDisplay,
        fld_Pre_HSDStartingHr: preStartHr,
        fld_Cur_HSDStartingHr: curStartHr,
        fld_Pre_HSDClosingHr: preCloseHr,
        fld_Cur_HSDClosingHr: curCloseHr,
        fld_Pre_HSDStartingKM: preStartKm,
        fld_Cur_HSDStartingKM: curStartKm,
        fld_Pre_HSDClosingKM: preCloseKm,
        fld_Cur_HSDClosingKM: curCloseKm,
        fld_username: row.fld_username ?? row.fld_userName ?? "",
      };
    });
  }, [rawData, selectedBranchId]);

  const loadDropdowns = useCallback(async () => {
    if (!selectedBranchId) {
      setVehicleOptions([]);
      return;
    }

    setLoadingDropdowns(true);
    try {
      const response = await axios.get(`${BASE}/hsd_dropdown`, {
        params: { branchId: selectedBranchId },
        headers: commonHeaders,
      });

      const vehicles = Array.isArray(response.data?.vehicleCodes)
        ? response.data.vehicleCodes
        : [];
      const branches = Array.isArray(response.data?.branches)
        ? response.data.branches
        : [];

      setVehicleOptions(
        vehicles.filter(
          (vehicle) => vehicle.fld_vehichlecode && vehicle.fld_vehichlecode !== "Select"
        )
      );
      setBranchList(
        branches.filter(
          (branchItem) => branchItem.fld_Branchid !== "" && branchItem.fld_Branchid != null
        )
      );
    } catch (error) {
      console.error("Dropdown fetch failed:", error);
      toast.error(error?.response?.data?.message || "Failed to load dropdown data");
      setVehicleOptions([]);
    } finally {
      setLoadingDropdowns(false);
    }
  }, [selectedBranchId, commonHeaders]);

  useEffect(() => {
    loadDropdowns();
  }, [loadDropdowns]);

  const fetchGrid = useCallback(
    async (showToast = true) => {
      if (!fromDate || !toDate) {
        toast.warning("Please Select From Date and To Date");
        return;
      }

      if (new Date(fromDate).getTime() > new Date(toDate).getTime()) {
        toast.warning("From Date should not be greater than To Date");
        return;
      }

      setLoadingGrid(true);
      try {
        const response = await axios.get(`${BASE}/hsd_entry_list`, {
          params: {
            branchId: selectedBranchId || 0,
            fromDate,
            toDate,
            vehicleCode: vehicleCode || "%",
          },
          headers: commonHeaders,
        });

        const records = Array.isArray(response.data?.data) ? response.data.data : [];
        setRawData(records);

        if (showToast) {
          if (records.length === 0) toast.info("No records found.");
          else toast.success(`Found ${records.length} record(s)`);
        }
      } catch (error) {
        console.error("HSD entry list fetch failed:", error);
        toast.error(error?.response?.data?.message || "Failed to fetch HSD records");
        setRawData([]);
      } finally {
        setLoadingGrid(false);
      }
    },
    [fromDate, toDate, selectedBranchId, vehicleCode, commonHeaders]
  );

  useEffect(() => {
    fetchGrid(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const initEdits = {};
    const initChecks = {};

    data.forEach((row, index) => {
      initEdits[index] = {
        curStartHr: isNegativeNumber(row.fld_Cur_HSDStartingHr)
          ? "0"
          : row.fld_Cur_HSDStartingHr ?? "",
        curCloseHr: isNegativeNumber(row.fld_Cur_HSDClosingHr)
          ? "0"
          : row.fld_Cur_HSDClosingHr ?? "",
        curStartKm: isNegativeNumber(row.fld_Cur_HSDStartingKM)
          ? "0"
          : row.fld_Cur_HSDStartingKM ?? "",
        curCloseKm: isNegativeNumber(row.fld_Cur_HSDClosingKM)
          ? "0"
          : row.fld_Cur_HSDClosingKM ?? "",
      };
      initChecks[index] = false;
    });

    setEditedRows(initEdits);
    setCheckedRows(initChecks);
    setSelectAll(false);
  }, [data]);

  const handleSearch = () => {
    fetchGrid(true);
  };

  const handleEditChange = (index, field, value) => {
    const cleaned = sanitizeNonNegative(value);
    setEditedRows((previous) => ({
      ...previous,
      [index]: {
        ...previous[index],
        [field]: cleaned,
      },
    }));
  };

  const handleEditKeyDown = (event) => {
    if (event.key === "-" || event.key === "Minus" || event.key === "e" || event.key === "E" || event.key === "+") {
      event.preventDefault();
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const updated = {};
    data.forEach((_, index) => {
      updated[index] = checked;
    });
    setCheckedRows(updated);
  };

  const handleRowCheck = (index, checked) => {
    setCheckedRows((previous) => {
      const updated = { ...previous, [index]: checked };
      const everySelected =
        data.length > 0 && data.every((_, rowIndex) => Boolean(updated[rowIndex]));
      setSelectAll(everySelected);
      return updated;
    });
  };

  const selectedCount = useMemo(
    () => Object.values(checkedRows).filter(Boolean).length,
    [checkedRows]
  );

  const handleSubmit = async () => {
    const selectedPairs = data
      .map((row, index) => ({ row, index }))
      .filter(({ index }) => checkedRows[index]);

    if (selectedPairs.length === 0) {
      toast.warning("Select at least one record.");
      return;
    }

    for (const { index } of selectedPairs) {
      const edits = editedRows[index] || {};
      const badField = EDIT_FIELDS.find((f) => isNegativeNumber(edits[f.key]));
      if (badField) {
        toast.error(`Row ${index + 1}: ${badField.label} cannot be negative`);
        return;
      }
    }

    const selectedEntries = selectedPairs.map(({ row, index }) => ({
      SIdetId: parseInt(row.fld_storesissuedetId, 10),
      branchId: parseInt(row.fld_branchid, 10) || 0,
      date: row.fld_date,
      vehicleCode:
        (row.fld_vehiclecode || "").split(" : ")[0].trim() || row.fld_vehiclecode,
      itemId: parseInt(row.fld_itemid, 10) || 0,
      preStartHr: row.fld_Pre_HSDStartingHr ?? 0,
      curStartHr: editedRows[index]?.curStartHr ?? 0,
      preCloseHr: row.fld_Pre_HSDClosingHr ?? 0,
      curCloseHr: editedRows[index]?.curCloseHr ?? 0,
      preStartKm: row.fld_Pre_HSDStartingKM ?? 0,
      curStartKm: editedRows[index]?.curStartKm ?? 0,
      preCloseKm: row.fld_Pre_HSDClosingKM ?? 0,
      curCloseKm: editedRows[index]?.curCloseKm ?? 0,
    }));

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${BASE}/vehicleHsdHourUpdate`,
        {
          userId: parseInt(userId, 10) || 0,
          entries: selectedEntries,
        },
        { headers: commonHeaders }
      );

      if (response.data?.success) {
        toast.success("Updated Successfully!");
        navigate("/inventory/VehicleHsdHourUpdationListing");
      } else {
        toast.error(response.data?.message || "Update failed.");
      }
    } catch (error) {
      console.error("Submit failed:", error);
      toast.error(error?.response?.data?.message || "Failed to save records.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderEditInput = (index, field) => (
    <input
      type="number"
      min="0"
      step="any"
      inputMode="decimal"
      value={editedRows[index]?.[field] ?? ""}
      onChange={(e) => handleEditChange(index, field, e.target.value)}
      onKeyDown={handleEditKeyDown}
      onPaste={(e) => {
        const text = e.clipboardData?.getData("text") || "";
        if (text.includes("-")) {
          e.preventDefault();
          handleEditChange(index, field, sanitizeNonNegative(text));
        }
      }}
      className={editInputClass}
      placeholder="Enter"
    />
  );

  return (
    <VehicleLayoutWrapper>
      <div className="w-full max-w-full min-w-0 box-border p-4 md:p-6">
        <HeaderBar
          title={<span className="font-dmserif text-3xl">HSD HOUR UPDATION ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className="space-y-6 mt-6 w-full max-w-full min-w-0">
          {/* ---------- Page header card ---------- */}
          <div className={cardClass}>
            <div
              className={`px-4 md:px-6 py-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md flex-shrink-0`}
                >
                  <FaGasPump className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                      HSD Hour Updation Entry
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {data.length} Records
                    </span>
                    {selectedCount > 0 && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark ? "bg-emerald-900/30 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        {selectedCount} Selected
                      </span>
                    )}
                    {(loadingGrid || loadingDropdowns) && (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-gray-700 text-gray-300"
                            : `bg-${baseColor}-50 text-${baseColor}-700`
                        }`}
                      >
                        <FaCog className="animate-spin" />
                        Loading...
                      </span>
                    )}
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    Update current starting and closing HSD hour / KM values for selected vehicles
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate("/inventory/VehicleHsdHourUpdationListing")}
                className={`${neutralButtonClass} self-start lg:self-auto`}
              >
                <FaArrowLeft />
                Back
              </button>
            </div>
          </div>

          {/* ---------- Filters card ---------- */}
          <div className={cardClass}>
            <div
              className={`px-4 md:px-6 py-4 border-b ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md flex-shrink-0`}
                >
                  <FaFilter className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    Filtration Area
                  </h3>
                  <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    Filter HSD entries by date, vehicle and branch
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="min-w-0">
                  <label className={labelClass}>
                    From Date <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>
                    To Date <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <select
                    value={vehicleCode}
                    onChange={(e) => setVehicleCode(e.target.value)}
                    disabled={!selectedBranchId || loadingDropdowns}
                    className={`${inputClass} ${!selectedBranchId ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <option value="">Select Vehicle</option>
                    {vehicleOptions.map((vehicle, index) => (
                      <option
                        key={`${vehicle.fld_vehichlecode}-${index}`}
                        value={vehicle.fld_vehichlecode}
                      >
                        {vehicle.fld_vehichlecode}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select
                    value={selectedBranchId}
                    onChange={(e) => {
                      setSelectedBranchId(parseInt(e.target.value, 10) || 0);
                      setVehicleCode("");
                      setRawData([]);
                    }}
                    className={inputClass}
                  >
                    <option value={0}>Select Branch</option>
                    {branchList.map((branchItem, index) => (
                      <option
                        key={branchItem.fld_Branchid || index}
                        value={branchItem.fld_Branchid}
                      >
                        {branchItem.fld_BranchName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                className={`flex justify-end items-center mt-6 pt-5 border-t ${
                  isDark ? "border-slate-700" : "border-slate-200"
                }`}
              >
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loadingGrid}
                  className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition whitespace-nowrap bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  {loadingGrid ? <FaCog className="animate-spin" /> : <FaSearch />}
                  {loadingGrid ? "Searching..." : "Search Records"}
                </button>
              </div>
            </div>
          </div>

          {/* ---------- Entry grid card ---------- */}
          <div className={cardClass}>
            <div className={`p-4 md:p-6 border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3 min-w-0">
                  <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade} flex-shrink-0`} />
                  <div className="min-w-0">
                    <h3 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                      HSD Hour Updation Entry
                    </h3>
                    <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Yellow/current columns are editable values
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {data.length} records
                  </span>
                </div>

                {data.length > 0 && (
                  <label
                    className={`inline-flex self-start lg:self-auto items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm font-semibold whitespace-nowrap ${
                      selectAll
                        ? isDark
                          ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                          : `bg-${baseColor}-50 text-${baseColor}-700`
                        : isDark
                        ? "bg-gray-800 text-gray-300"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className={`w-4 h-4 accent-${baseColor}-600 cursor-pointer`}
                    />
                    <FaCheckSquare />
                    Select All
                  </label>
                )}
              </div>
            </div>

            {loadingGrid ? (
              <div className="text-center py-16">
                <FaCog className={`w-10 h-10 mx-auto animate-spin text-${baseColor}-500`} />
                <p className={`mt-3 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Loading HSD Hour records...
                </p>
              </div>
            ) : data.length > 0 ? (
              <>
                <div className="w-full">
                  <div className="overflow-x-auto overscroll-x-contain" style={tableScrollStyle}>
                    <table className="w-full min-w-[1400px] text-sm">
                      <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                        <tr>
                          <th className="px-3 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">S.No</th>
                          <th className="px-3 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">Date</th>
                          <th className="px-3 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">Issue Qty</th>
                          <th className="px-3 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">Vehicle Code : Name</th>
                          <th className="px-3 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">Pre Starting Hr</th>
                          <th className="px-3 py-3 text-center text-xs font-bold uppercase whitespace-nowrap text-yellow-200">Cur Starting Hr ✎</th>
                          <th className="px-3 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">Pre Closing Hr</th>
                          <th className="px-3 py-3 text-center text-xs font-bold uppercase whitespace-nowrap text-yellow-200">Cur Closing Hr ✎</th>
                          <th className="px-3 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">Pre Starting KM</th>
                          <th className="px-3 py-3 text-center text-xs font-bold uppercase whitespace-nowrap text-yellow-200">Cur Starting KM ✎</th>
                          <th className="px-3 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">Pre Closing KM</th>
                          <th className="px-3 py-3 text-center text-xs font-bold uppercase whitespace-nowrap text-yellow-200">Cur Closing KM ✎</th>
                          <th className="px-3 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">User Name</th>
                          <th className="px-3 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">Select</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? "divide-gray-700" : "divide-gray-200"}`}>
                        {data.map((item, index) => {
                          const checked = Boolean(checkedRows[index]);
                          return (
                            <tr
                              key={item.fld_storesissuedetId ?? index}
                              className={`transition-colors ${
                                checked
                                  ? isDark
                                    ? `bg-${baseColor}-900/30`
                                    : `bg-${baseColor}-50`
                                  : tableRowClass
                              }`}
                            >
                              <td className={`${tableCellClass} text-center font-medium`}>{index + 1}</td>
                              <td className={tableCellClass}>
                                <div className="flex items-center gap-2">
                                  <FaClock className={`text-${baseColor}-500`} />
                                  <span>{showVal(item.fld_date)}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right whitespace-nowrap">
                                <span
                                  className={`inline-flex min-w-[65px] justify-end px-3 py-1.5 rounded-lg font-semibold ${
                                    isDark ? "bg-gray-700/60 text-gray-200" : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {showVal(item.fld_qty)}
                                </span>
                              </td>
                              <td className={tableCellClass}>
                                <div className="flex items-center gap-2">
                                  <FaCar className={`text-${baseColor}-500 flex-shrink-0`} />
                                  <span className="font-semibold">{showVal(item.fld_vehiclecodename)}</span>
                                </div>
                              </td>
                              <td className={`${readOnlyCellClass} text-right`}>
                                {showVal(item.fld_Pre_HSDStartingHr)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {renderEditInput(index, "curStartHr")}
                              </td>
                              <td className={`${readOnlyCellClass} text-right`}>
                                {showVal(item.fld_Pre_HSDClosingHr)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {renderEditInput(index, "curCloseHr")}
                              </td>
                              <td className={`${readOnlyCellClass} text-right`}>
                                {showVal(item.fld_Pre_HSDStartingKM)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {renderEditInput(index, "curStartKm")}
                              </td>
                              <td className={`${readOnlyCellClass} text-right`}>
                                {showVal(item.fld_Pre_HSDClosingKM)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {renderEditInput(index, "curCloseKm")}
                              </td>
                              <td className={tableCellClass}>{showVal(item.fld_username)}</td>
                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => handleRowCheck(index, e.target.checked)}
                                  className={`w-5 h-5 accent-${baseColor}-600 cursor-pointer`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  className={`p-4 md:p-6 border-t flex flex-col md:flex-row md:items-center justify-between gap-4 min-w-0 ${
                    isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-gray-50/50"
                  }`}
                >
                  <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {selectedCount > 0 ? (
                      <>
                        <span className="font-semibold">{selectedCount}</span> record(s) selected for update
                      </>
                    ) : (
                      "Select at least one record before submitting."
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting || selectedCount === 0}
                    className={`inline-flex self-start md:self-auto items-center gap-2 px-7 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition whitespace-nowrap bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isSubmitting ? <FaCog className="animate-spin" /> : <FaSave />}
                    {isSubmitting
                      ? "Saving..."
                      : `Submit Selected${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-14">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    isDark ? "bg-gray-800" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className={`font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No HSD Records Found
                </h3>
                <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Select the required filters and click Search Records.
                </p>
              </div>
            )}
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleHsdHrUpdation;